import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import OutreachModal from './components/OutreachModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Matches timeline — shows matches in both perspectives:
 *  - "wanter" — sellers scanned an item on YOUR wanted list
 *  - "seller" — people want an item YOU scanned; from here you can email them
 *
 * Grouped by (perspective, barcode) so a seller can fire one outreach burst per barcode.
 */
export default function MatchesPage() {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('wanter'); // 'wanter' | 'seller'
  const [outreachGroup, setOutreachGroup] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/wanted-list/matches/my`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAll(data.items || []);
      } else if (res.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markSeen = async (match_id) => {
    await fetch(`${API}/api/wanted-list/matches/${match_id}/mark-seen`, {
      method: 'POST',
      credentials: 'include',
    });
    setAll((prev) => prev.map((m) => (m.match_id === match_id ? { ...m, unread: false } : m)));
  };

  const block = async (user_id) => {
    if (!window.confirm('Block this user? You won\'t see matches with them.')) return;
    await fetch(`${API}/api/wanted-list/block/${user_id}`, { method: 'POST', credentials: 'include' });
    refresh();
  };

  // Group seller-perspective matches by barcode so one outreach burst can target all wanters.
  const sellerGroups = useMemo(() => {
    const groups = {};
    all.filter((m) => m.perspective === 'seller').forEach((m) => {
      if (!groups[m.barcode]) groups[m.barcode] = { barcode: m.barcode, title: m.title, matches: [] };
      groups[m.barcode].matches.push(m);
    });
    // Sort each group's matches DESC by wanter_max_price (top payer first).
    Object.values(groups).forEach((g) => g.matches.sort((a, b) => (b.wanter_max_price || 0) - (a.wanter_max_price || 0)));
    return Object.values(groups);
  }, [all]);

  const wanterItems = all.filter((m) => m.perspective === 'wanter');

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 pb-20">
      <header className="border-b border-zinc-800 bg-black/40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-zinc-400 hover:text-white"
            data-testid="matches-back"
          >
            ← Dashboard
          </button>
          <h1 className="text-lg font-extrabold text-white">Community Matches</h1>
          <button
            onClick={() => navigate('/wanted-list')}
            className="text-sm font-bold text-[#FFD700]"
            data-testid="matches-goto-wanted"
          >
            Wanted →
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-4">
        <div className="flex gap-2 mb-5" data-testid="matches-tabs">
          <button
            onClick={() => setTab('wanter')}
            className={`px-4 py-2 rounded-full text-sm font-bold ${tab === 'wanter' ? 'bg-[#00D26A] text-black' : 'bg-zinc-900 text-zinc-300 border border-zinc-800'}`}
            data-testid="tab-wanter"
          >
            I want ({wanterItems.length})
          </button>
          <button
            onClick={() => setTab('seller')}
            className={`px-4 py-2 rounded-full text-sm font-bold ${tab === 'seller' ? 'bg-[#FFD700] text-black' : 'bg-zinc-900 text-zinc-300 border border-zinc-800'}`}
            data-testid="tab-seller"
          >
            I can sell ({sellerGroups.length})
          </button>
        </div>

        {loading && <p className="text-zinc-500">Loading…</p>}

        {!loading && tab === 'wanter' && (
          wanterItems.length === 0 ? (
            <p className="text-zinc-500 italic" data-testid="matches-wanter-empty">
              No matches on your wanted list yet.
            </p>
          ) : (
            <ul className="space-y-3" data-testid="matches-wanter-list">
              {wanterItems.map((m) => (
                <li
                  key={m.match_id}
                  className={`border rounded-xl p-4 ${m.unread ? 'border-[#FFD700] bg-[#1a1500]' : 'border-zinc-800 bg-zinc-900'}`}
                  data-testid={`match-wanter-${m.match_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-white truncate">{m.title}</p>
                      <p className="text-xs text-zinc-500 font-mono mt-1">{m.barcode}</p>
                      <p className="text-sm mt-2">
                        Seller: <span className="text-[#00D26A] font-semibold">{m.seller_username}</span>
                        <span className="text-zinc-500 ml-3">Best buyer £{(m.seller_prices?.gold_cash_equiv ?? 0).toFixed(2)}</span>
                      </p>
                      {m.ebay_url && (
                        <a
                          href={m.ebay_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block mt-2 text-sm font-bold text-[#FFD700] underline"
                          data-testid={`match-ebay-link-${m.match_id}`}
                        >
                          View on eBay →
                        </a>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      {m.unread && (
                        <button
                          onClick={() => markSeen(m.match_id)}
                          className="text-xs font-bold text-zinc-300 border border-zinc-700 px-2 py-1 rounded"
                          data-testid={`mark-seen-${m.match_id}`}
                        >
                          Mark seen
                        </button>
                      )}
                      <button
                        onClick={() => block(m.seller_user_id)}
                        className="text-[10px] text-zinc-500 hover:text-red-400"
                        data-testid={`block-${m.seller_user_id}`}
                      >
                        Block seller
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )
        )}

        {!loading && tab === 'seller' && (
          sellerGroups.length === 0 ? (
            <p className="text-zinc-500 italic" data-testid="matches-seller-empty">
              Nothing matched against your scans yet. Keep scanning.
            </p>
          ) : (
            <ul className="space-y-4" data-testid="matches-seller-list">
              {sellerGroups.map((g) => (
                <li
                  key={g.barcode}
                  className="border border-zinc-800 bg-zinc-900 rounded-xl p-4"
                  data-testid={`seller-group-${g.barcode}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="font-bold text-white truncate">{g.title || '(untitled)'}</p>
                      <p className="text-xs text-zinc-500 font-mono">{g.barcode}</p>
                    </div>
                    <span
                      className="text-[11px] font-black px-2 py-1 rounded"
                      style={{ background: '#FFD700', color: '#050505' }}
                    >
                      {g.matches.length} WANTER{g.matches.length === 1 ? '' : 'S'}
                    </span>
                  </div>
                  <div className="text-sm text-zinc-400 mb-3">
                    Top max price: <span className="text-[#00D26A] font-bold">£{(g.matches[0]?.wanter_max_price || 0).toFixed(2)}</span>
                  </div>
                  <button
                    onClick={() => setOutreachGroup(g)}
                    className="px-4 py-2 rounded-full font-bold text-sm"
                    style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
                    data-testid={`outreach-open-${g.barcode}`}
                  >
                    📧 Email wanters
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
      </div>

      {outreachGroup && (
        <OutreachModal
          group={outreachGroup}
          onClose={() => setOutreachGroup(null)}
          onSent={() => {
            setOutreachGroup(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
